import { useMapControl } from "features/map/control/useMapControl"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Enable } from "re-resizable"
import { footerHeight, navbarHeight, PanoramaView, sidePanelMinWidth } from "@nova/constants"
import { useAppDispatch } from "integrations/redux/hooks"
import { zoomToShowPoint } from "features/map/control/slice"
import { PanoramaImage } from "@nova/protobuf"
import { WebMercatorViewport } from "integrations/deckgl"
import { getViewState } from "features/map/GlobalViewState"
import { useResizableSidePanelWidth } from "panels/content/side-panel/SidePanel"

export type Dimensions = {
    width: number
    height: number
}

type usePanoramaProps = {
    selectedView: PanoramaView
}

const DEFAULT_WIDTH = 475
const DEFAULT_HEIGHT = 300
const baseWidth = `100vw`
const baseHeight = `100vh - ${navbarHeight}px`

let panoSplitHeight =
    typeof window !== "undefined" ? 0.67 * (window.innerHeight - navbarHeight - footerHeight) : 0

export const usePanorama = ({ selectedView }: usePanoramaProps) => {
    const dispatch = useAppDispatch()
    const { checkIfCoordinateIsOffscreen } = useMapControl()

    const [vh, setVh] = useState(window.innerHeight / 100)
    const [splitHeight, setSplitHeight] = useState(0.67 * (100 * vh - navbarHeight - footerHeight))
    const [dimensions, setDimensions] = useState({
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
    })
    const [startSplitHeight, setStartSplitHeight] = useState(splitHeight)
    const [startDimensions, setStartDimensions] = useState({
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
    })

    const sidePanelWidth = useResizableSidePanelWidth()

    const [width, height, size, resizableEdges] = useMemo(() => {
        // Dimensions of the usable/draggable space of the container.
        let width: string
        let height: string

        // Dimensions of the container itself.
        let size: { width: number | string; height: number | string }

        let resizableEdges: Enable | undefined | false

        switch (selectedView) {
            case PanoramaView.Mini:
                width = `calc(${baseWidth} - ${sidePanelMinWidth}px - 24px)`
                height = `calc(${baseHeight} - ${footerHeight}px - 24px)`
                size = dimensions
                resizableEdges = {
                    right: true,
                    bottomRight: true,
                    bottom: true,
                }
                break
            case PanoramaView.Split:
                width = `calc(${baseWidth} - ${sidePanelWidth}px)`
                height = `calc(${baseHeight} - ${footerHeight}px - 16px)`
                size = {
                    width: "100%",
                    height: splitHeight,
                }
                resizableEdges = { bottom: true }
                break
            case PanoramaView.Full:
                width = baseWidth
                height = `calc(${baseHeight})`
                size = {
                    width: "100%",
                    height: "100%",
                }
                resizableEdges = false
                break
        }

        return [width!, height!, size!, resizableEdges]
    }, [selectedView, dimensions, sidePanelWidth, splitHeight])

    const pointIsOffMap = useCallback(
        (image?: PanoramaImage) => {
            if (!image?.longitude || !image.latitude) return
            return checkIfCoordinateIsOffscreen(undefined, [image?.longitude, image?.latitude])
        },
        [checkIfCoordinateIsOffscreen]
    )

    const zoomToPointOnMap = useCallback(
        (image?: PanoramaImage) => {
            if (!image?.longitude || !image?.latitude || !pointIsOffMap(image)) return
            const [lng, lat] = [image.longitude, image.latitude]

            if (selectedView !== PanoramaView.Split) {
                dispatch(zoomToShowPoint({ lng, lat, zoomLevel: 14 }))
                return
            }

            const idealX = window.innerWidth / 2 - sidePanelWidth
            const idealY =
                panoSplitHeight + (window.innerHeight - panoSplitHeight) / 2 - footerHeight

            const centerX = window.innerWidth / 2
            const centerY = window.innerHeight / 2

            const viewport = new WebMercatorViewport({ ...getViewState(), zoom: 14 })

            let [x, y] = viewport.project([lng, lat])
            x -= idealX - centerX
            y -= idealY - centerY

            const [newLng, newLat] = viewport.unproject([x, y])
            dispatch(zoomToShowPoint({ lng: newLng, lat: newLat, zoomLevel: 14 }))
        },
        [dispatch, pointIsOffMap, selectedView, sidePanelWidth]
    )

    const onResize = useCallback(
        (d: Dimensions) => {
            switch (selectedView) {
                case PanoramaView.Mini:
                    setDimensions({
                        width: startDimensions.width + d.width,
                        height: startDimensions.height + d.height,
                    })
                    break
                case PanoramaView.Split:
                    setSplitHeight(startSplitHeight + d.height)
                    panoSplitHeight = startSplitHeight + d.height
                    break
            }
        },
        [selectedView, startDimensions, startSplitHeight]
    )

    useEffect(() => {
        const handleResize = () => {
            setVh(window.innerHeight / 100)
        }

        window.addEventListener("resize", handleResize)

        return () => {
            window.removeEventListener("resize", handleResize)
        }
    }, [])

    return {
        width,
        height,
        size,
        dimensions,
        splitHeight,
        resizableEdges,
        pointIsOffMap,
        zoomToPointOnMap,
        onResize,
        setStartDimensions,
        setStartSplitHeight,
    }
}
